import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { defaultFilters, DraftLeadFilterSet } from './filters'
import { DraftLead, DraftLeadPageState } from './typings'
import {
  bulkDeleteDraftLeads,
  deleteDraftLead,
  getDraftLeadList,
  saveAndAutoReviewDraftLead,
} from './api'
import { setOrderBy, updateFilterState } from './helpers'
import { deserializeFilters } from '@user/components/Filters'
import { StoreData } from '../reducers/typings'
import { loadFilterPreferences } from '../userPreferences/api'
import { createLead } from '../lead/api'
import { CardListViewMode } from '@sales-center/components'
import { pageSizeOptions } from '@user/components/xGrid'

export const draftLeadAdapter = createEntityAdapter<DraftLead>()

export const initialState =
  draftLeadAdapter.getInitialState<DraftLeadPageState>({
    page: 1,
    perPage: 50,
    count: 0,
    loading: true,
    shouldFetchList: false,
    filterSet: defaultFilters,
    mode: 'card',
    multipleServiceLocations: false,
  })

export const draftLeadSlice = createSlice({
  name: 'draftLeads',
  initialState,
  reducers: {
    setDraftLeadPage: (state, { payload: page }: PayloadAction<number>) => {
      state.page = page > 0 ? page : 1
      state.shouldFetchList = true
    },
    setDraftLeadsPerPage: (state, { payload }: PayloadAction<number>) => {
      state.page = 1
      state.perPage = payload
      state.shouldFetchList = true
    },
    setDraftLeadPageMode: (
      state,
      { payload }: PayloadAction<CardListViewMode>,
    ) => {
      state.mode = payload
      state.page = 1
      if (payload === 'list') {
        state.perPage =
          state.perPage > pageSizeOptions[0]
            ? state.perPage
            : pageSizeOptions[0]
      } else {
        state.perPage = 10
      }
      state.shouldFetchList = true

      setOrderBy(state, state.filterSet.orderBy)

      draftLeadAdapter.removeAll(state)
    },
    setDraftLeadFilters: (
      state,
      { payload }: PayloadAction<DraftLeadFilterSet>,
    ) => updateFilterState(state, payload),
  },
  extraReducers: (builder) => {
    builder.addCase(loadFilterPreferences.fulfilled, (state, { payload }) => {
      const draftPref = payload.find((p) => p.type === 'draft')
      updateFilterState(
        state,
        draftPref
          ? deserializeFilters<DraftLead, DraftLeadFilterSet>(
              defaultFilters,
              draftPref.filter,
            )
          : defaultFilters,
      )
    })
    builder.addCase(getDraftLeadList.pending, (state) => {
      state.loading = true
      state.shouldFetchList = false
    })
    builder.addCase(getDraftLeadList.rejected, (state) => {
      state.loading = false
      state.count = 0
      state.page = 1
      draftLeadAdapter.removeAll(state)
    })
    builder.addCase(getDraftLeadList.fulfilled, (state, { payload }) => {
      const { '@odata.count': count, value } = payload
      state.loading = false
      if (count !== undefined) {
        state.count = count
      }

      // Convert dates
      value.map((i) => {
        if (i.createdTime !== null) i.createdTime = new Date(i.createdTime)
      })
      draftLeadAdapter.setAll(state, value)
    })
    builder.addCase(createLead.fulfilled, (state) => {
      state.shouldFetchList = true
    })
    builder.addCase(deleteDraftLead.fulfilled, (state) => {
      state.shouldFetchList = true
    })
    builder.addCase(
      saveAndAutoReviewDraftLead.fulfilled,
      (state, { payload }) => {
        if (payload.converted) state.shouldFetchList = true
      },
    )
    //#region bulk actions
    builder.addCase(bulkDeleteDraftLeads.fulfilled, (state) => {
      state.shouldFetchList = true
    })
    //#endregion
  },
})

export default draftLeadSlice.reducer

export const {
  setDraftLeadPage,
  setDraftLeadFilters,
  setDraftLeadPageMode,
  setDraftLeadsPerPage,
} = draftLeadSlice.actions

export const draftLeadAdapterSelectors =
  draftLeadAdapter.getSelectors<StoreData>((state) => state.draftLeads)

export const {
  selectById: selectDraftLeadById,
  selectIds: selectDraftLeadIds,
  selectEntities: selectDraftLeadEntities,
  selectAll: selectAllDraftLeads,
  selectTotal: selectTotalDraftLeads,
} = draftLeadAdapterSelectors

export const selectLeadDetail = (state: StoreData) => {
  return state.leadsState.detail
}

export const selectDraftLeadPageState = (state: StoreData) => {
  const {
    draftLeads: {
      page,
      perPage,
      count,
      loading,
      shouldFetchList,
      mode,
      multipleServiceLocations,
    },
  } = state

  return {
    page,
    pages: Math.ceil(count / perPage),
    perPage,
    count,
    loading,
    shouldFetchList,
    mode,
    multipleServiceLocations,
  }
}

export const selectDraftLeadFilters = (state: StoreData) => {
  const {
    draftLeads: { filterSet },
  } = state
  return { filterSet }
}
